import React, { useState, useMemo } from "react";
import moment from "moment-timezone";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { type CfrSuggestion, type ListCfrTemplateItem, useCreateBulkInstances, BulkSendInstancesValidation } from "@/lib/api/cfr";
import { CommodityOptions } from "@/lib/api/commodity";
import { getUniqueStrings } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { chunkArray } from "@/lib/prelude";

interface BulkUpdatedReportSendDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedSuggestions: CfrSuggestion[];
    templateData: ListCfrTemplateItem[];
}

export function BulkUpdatedReportSendDialog({
    open,
    onOpenChange,
    selectedSuggestions,
    templateData,
}: BulkUpdatedReportSendDialogProps) {
    const { toast } = useToast();
    const createBulkInstances = useCreateBulkInstances();
    const queryClient = useQueryClient();

    // Get unique crop years from templates
    const cropYearOptions = useMemo(() => {
        const uniqueYears = Array.from(new Set(templateData.map(t => t.crop_year)))
            .sort((a, b) => b - a);
        return uniqueYears.map(year => ({
            value: year.toString(),
            label: year.toString(),
        }));
    }, [templateData]);

    const currentYear = moment().year();
    const [selectedCropYear, setSelectedCropYear] = useState<string>(
        selectedSuggestions[0]?.crop_year?.toString() ||
        cropYearOptions.find(opt => opt.value === currentYear.toString())?.value ||
        cropYearOptions[0]?.value ||
        currentYear.toString()
    );
    const [isTestSend, setIsTestSend] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Progress tracking state
    const [currentBatch, setCurrentBatch] = useState(0);
    const [totalBatches, setTotalBatches] = useState(0);
    const [successfulInstances, setSuccessfulInstances] = useState(0);
    const [failedInstances, setFailedInstances] = useState(0);

    // Get unique commodities from selected suggestions (read-only)
    const uniqueCommodities = useMemo(() => {
        const commodityValues = getUniqueStrings(
            selectedSuggestions
                .map(s => s.commodity)
                .filter((c): c is string => !!c)
        );
        return commodityValues.map(commodity => {
            const option = CommodityOptions.find(opt => opt.value === commodity);
            return {
                value: commodity,
                label: option?.label || commodity,
            };
        }).sort((a, b) => a.label.localeCompare(b.label));
    }, [selectedSuggestions]);

    // Template matching logic with validation
    const matchingResults = useMemo(() => {
        const selectedYear = parseInt(selectedCropYear);
        const validMatches: Array<{
            suggestion: CfrSuggestion;
            template: ListCfrTemplateItem;
        }> = [];
        const unmatchedSuggestions: CfrSuggestion[] = [];
        const ambiguousSuggestions: Array<{
            suggestion: CfrSuggestion;
            templates: ListCfrTemplateItem[];
        }> = [];

        for (const suggestion of selectedSuggestions) {
            // Find templates matching crop year, commodity, and customer_destination_id
            const matchingTemplates = templateData.filter(template =>
                template.crop_year === (suggestion.crop_year || selectedYear) &&
                template.commodity_type === suggestion.commodity &&
                template.customer_destination_id === suggestion.destination_id
            );

            if (matchingTemplates.length === 0) {
                unmatchedSuggestions.push(suggestion);
            } else if (matchingTemplates.length === 1) {
                validMatches.push({
                    suggestion,
                    template: matchingTemplates[0],
                });
            } else {
                ambiguousSuggestions.push({
                    suggestion,
                    templates: matchingTemplates,
                });
            }
        }

        return {
            validMatches,
            unmatchedSuggestions,
            ambiguousSuggestions,
        };
    }, [selectedSuggestions, templateData, selectedCropYear]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isSubmitting) return;
        setIsSubmitting(true);

        // Reset progress tracking
        setCurrentBatch(0);
        setTotalBatches(0);
        setSuccessfulInstances(0);
        setFailedInstances(0);

        try {
            if (selectedSuggestions.length === 0) {
                toast({
                    title: "Error",
                    description: "No suggestions selected",
                    variant: "destructive",
                });
                return;
            }

            if (matchingResults.validMatches.length === 0) {
                toast({
                    title: "Error",
                    description: "No valid template matches found. All suggestions are either unmatched or ambiguous.",
                    variant: "destructive",
                });
                setIsSubmitting(false);
                return;
            }

            // Split valid matches into batches of 10
            const BATCH_SIZE = 10;
            const batches = chunkArray(BATCH_SIZE, matchingResults.validMatches);
            setTotalBatches(batches.length);

            let totalSuccessful = 0;
            let totalFailed = 0;

            // Process each batch sequentially
            for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
                const batch = batches[batchIndex];
                setCurrentBatch(batchIndex + 1);

                try {
                    // Build the to_send array for this batch
                    const to_send = batch.map(({ suggestion, template }) => ({
                        cfr_document_id: template.id,
                        group_id: uuidV4(),
                        template_id: template.DocumentTemplate!.id,
                        customer_id: suggestion.customer_id!,
                        is_test_send: isTestSend,
                        is_draft: false,
                        suggestion_id: suggestion.id,
                        meta_fields: {
                            contract_number: suggestion.contract_number,
                            contract_type: suggestion.original_meta?.contract_type,
                            contract_shipping_dates: suggestion.original_meta?.shipment_range,
                            contract_quantity: suggestion.contract_quantity,
                        },
                    }));

                    // Validate the payload
                    const parseResult = BulkSendInstancesValidation.safeParse({ to_send });

                    if (!parseResult.success) {
                        console.error("Failed to validate bulk send data for batch", batchIndex + 1, parseResult.error);
                        totalFailed += to_send.length;
                        setFailedInstances(totalFailed);
                        continue; // Skip this batch but continue with others
                    }

                    // Call the API for this batch
                    const result = await createBulkInstances(parseResult.data);

                    if (result.success) {
                        totalSuccessful += to_send.length;
                        setSuccessfulInstances(totalSuccessful);
                    } else {
                        console.error("Failed to send batch", batchIndex + 1, result);
                        totalFailed += to_send.length;
                        setFailedInstances(totalFailed);
                    }
                } catch (error) {
                    console.error("Error processing batch", batchIndex + 1, error);
                    // Count failed instances for this batch
                    const batchInstanceCount = batch.length;
                    totalFailed += batchInstanceCount;
                    setFailedInstances(totalFailed);
                    // Continue with next batch
                }
            }

            queryClient.invalidateQueries({ queryKey: ["cfr"] });

            // Show final summary toast
            if (totalFailed === 0) {
                toast({
                    title: "Bulk Send Completed",
                    description: `Successfully queued ${totalSuccessful} contract(s)`,
                });
            } else if (totalSuccessful === 0) {
                toast({
                    title: "Bulk Send Failed",
                    description: `Failed to queue all ${totalFailed} contracts`,
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Bulk Send Partially Completed",
                    description: `Queued ${totalSuccessful} contract(s). ${totalFailed} failed.`,
                    variant: "destructive",
                });
            }

            // Reset form and close dialog on success
            if (totalSuccessful > 0) {
                onOpenChange(false);
            }

        } catch (error) {
            console.error("Failed to queue bulk instances", error);
            const errorMessage = error instanceof Error ? error.message : "Failed to queue bulk instances";
            toast({
                title: "Error",
                description: errorMessage,
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[550px]">
                <DialogHeader>
                    <DialogTitle>Bulk Send CFR Templates (Updated Report)</DialogTitle>
                </DialogHeader>

                {isSubmitting ? (
                    <div className="grid gap-4 py-8">
                        <div className="text-center space-y-4">
                            <div className="text-lg font-semibold">Sending Contracts...</div>
                            
                            <div className="space-y-2">
                                <div className="text-sm text-muted-foreground">
                                    Processing batch {currentBatch} of {totalBatches}
                                </div>
                                
                                {/* Progress bar */}
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div 
                                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                        style={{ 
                                            width: `${totalBatches > 0 ? (currentBatch / totalBatches) * 100 : 0}%` 
                                        }}
                                    ></div>
                                </div>
                                
                                <div className="text-sm text-muted-foreground">
                                    {totalBatches > 0 ? Math.round((currentBatch / totalBatches) * 100) : 0}% complete
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-4">
                                <div className="text-center p-3 bg-green-50 rounded-lg">
                                    <div className="text-sm text-muted-foreground">Successful</div>
                                    <div className="text-2xl font-bold text-green-600">{successfulInstances}</div>
                                </div>
                                <div className="text-center p-3 bg-red-50 rounded-lg">
                                    <div className="text-sm text-muted-foreground">Failed</div>
                                    <div className="text-2xl font-bold text-red-600">{failedInstances}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="grid gap-4 py-4">
                            <div className="grid gap-2">
                                <Label>Selected Suggestions</Label>
                                <div className="text-sm text-muted-foreground">
                                    {selectedSuggestions.length} suggestion(s) selected
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="is_test_send">Is Test Send</Label>
                                <Checkbox
                                    id="is_test_send"
                                    checked={isTestSend}
                                    onCheckedChange={(checked) => setIsTestSend(!!checked)}
                                />
                            </div>

                            <div className="grid gap-2">
                                <Label>Commodities (from selected suggestions)</Label>
                                <div className="flex flex-wrap gap-2">
                                    {uniqueCommodities.length > 0 ? (
                                        uniqueCommodities.map((commodity) => (
                                            <Badge key={commodity.value} variant="secondary">
                                                {commodity.label}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-sm text-muted-foreground">No commodities found</span>
                                    )}
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label>Template Matching Results</Label>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        <span className="font-medium text-green-600">
                                            {matchingResults.validMatches.length} valid match(es)
                                        </span>
                                        <span className="text-muted-foreground">- Will be queued</span>
                                    </div>
                                    {matchingResults.unmatchedSuggestions.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                                            <span className="font-medium text-yellow-600">
                                                {matchingResults.unmatchedSuggestions.length} unmatched
                                            </span>
                                            <span className="text-muted-foreground">- No template found</span>
                                        </div>
                                    )}
                                    {matchingResults.ambiguousSuggestions.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4 text-red-600" />
                                            <span className="font-medium text-red-600">
                                                {matchingResults.ambiguousSuggestions.length} ambiguous
                                            </span>
                                            <span className="text-muted-foreground">- Multiple templates found</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="submit" 
                                disabled={isSubmitting || matchingResults.validMatches.length === 0}
                            >
                                {isSubmitting ? "Sending..." : `Send ${matchingResults.validMatches.length} Contract(s)`}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

